import { useLazyQuery } from "@apollo/client";
import cn from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";

import { GET_ALL_PAGES_USER } from "@api/queriesV2/cms";
import BlockTitle from "@components/cms/BlockTitle/BlockTitle";
import { getPageTypes } from "@config/cms";
import { blurDataURL } from "@config/config";
import { useIsMounted } from "@hooks/useIsMounted";
import { useStore } from "@hooks/useZustandProvider";
import i from "@styles/icons.module.scss";
import Container from "@ui-kit/Container/Container";
import Loading from "@ui-kit/Loading/Loading";
import { getPageImage } from "@utils/utils";

import s from "./StoryListBlock.module.scss";

const StoryListBlock = ({ data, settings, updateField }) => {
  const isMounted = useIsMounted();
  const { t } = useTranslation(["common"]);
  const { blockType } = settings.common;
  const { title } = data;
  const { cms, getBlockLocale } = useStore();
  const { isEditMode } = cms.uiOptions;
  const { locale } = useRouter();
  const lang = getBlockLocale(locale);

  const [storiesList, setStoriesList] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [showMore, setShowMore] = useState(true);
  const PAGE_SIZE = 6;

  const onLoadMore = () => {
    setPageNumber(pageNumber + 1);
  };

  const [getStories, { loading }] = useLazyQuery(GET_ALL_PAGES_USER, {
    context: { clientName: "v2" },
    variables: {
      language: isEditMode ? lang : locale,
      type: getPageTypes().story,
      pageNumber,
      pageSize: PAGE_SIZE,
    },
    onCompleted: (data) => {
      setStoriesList((state) => [...state, ...data.getAllPagesUser]);
    },
    onError: (error) => {
      toast.error(`[${error.name}]: ${error.message}`);
    },
  });

  useEffect(() => {
    getStories();
  }, [pageNumber]);

  useEffect(() => {
    if (isMounted) {
      setStoriesList([]);
    }
  }, [lang, isMounted]);

  useEffect(() => {
    setShowMore(pageNumber * PAGE_SIZE === storiesList.length);
  }, [storiesList.length]);

  return (
    <Container className={s.stories}>
      <Container full={false}>
        <section>
          <BlockTitle
            settings={settings}
            field={{ title }}
            onChange={updateField}
            type={blockType}
          />
          {pageNumber === 1 && loading ? (
            <Loading />
          ) : storiesList?.length ? (
            <div className={s.storiesList}>
              {storiesList.map((story, key) => {
                const { isPublished } = story;
                const { slug } = story.data;
                const { thumbnail, title } = story.data.seo;

                const image = getPageImage(story.data);

                return (
                  <Link key={key} href={`/stories/${slug}`} className={s.story}>
                    {thumbnail ? (
                      <div className={s.image}>
                        <Image
                          className={s.img}
                          fill
                          objectFit="cover"
                          quality={100}
                          src={image}
                          blurDataURL={blurDataURL}
                          alt={title}
                        />
                      </div>
                    ) : (
                      <div className={s.storyBanner}>
                        <div>story</div>
                        <div>banner</div>
                      </div>
                    )}
                    <h5
                      className={cn(s.itemTitle, !isPublished && s.unpublished)}
                    >
                      {title}
                    </h5>
                    <div className={s.read}>
                      {!isPublished && (
                        <div className={!isPublished && s.unpublishedText}>
                          {t("unpublished")}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
              {showMore && (
                <div className={s.showMoreWrap}>
                  {loading ? (
                    <Loading />
                  ) : (
                    <div className={s.showMore} onClick={onLoadMore}>
                      {t("show more")}
                      <icon className={i.arrowRight_v2} />
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>No stories yet.</div>
          )}
        </section>
      </Container>
    </Container>
  );
};

export default StoryListBlock;
